import { useCallback, useRef } from "react";
import { AuthLibMSALTokenAcquisitionError, getFreshTokensAndClaims, useMSALPayload } from "@initia-digital/auth-client-utils";
import { useQuery } from "@tanstack/react-query";
import { NavigationClient } from "@azure/msal-browser";
import { IdTokenClaims } from "@azure/msal-common";
import * as Sentry from "@sentry/react";

import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";

import { fetchEquipmentClassList, fetchHazardClassList, fetchHazardList, fetchIdentityByEmail, fetchPlanDetail, fetchPrestartDetail, searchJobList } from "../api/query";
import { fetchMsGroup } from "../api/query";
import { NavigateFunction, useNavigate } from "react-router-dom";


// Custom Navigation Client for MSAL that uses React Router
class CustomNavigationClient extends NavigationClient {
    // ---------------------
    private navigate: NavigateFunction;

    constructor(navigate: NavigateFunction) {
        super();
        this.navigate = navigate;
    }

     /**
     * Navigates to other pages within the same web application
     * You can use the useNavigate hook provided by react-router-dom to take advantage of client-side routing
     * @param url
     * @param options
     */
    async navigateInternal(url: string, options: { noHistory?: boolean }) {
        // ---------------------
        const relativePath = url.replace(window.location.origin, '');
        if (options.noHistory) {
            this.navigate(relativePath, { replace: true });
        } else {
            this.navigate(relativePath);
        }
        return false;
    }
}

export const useMSALNavigationSetup = () => {
    // ---------------------
    const navigate = useNavigate();
    
    return useCallback((pca: ReturnType<typeof useMSALPayload>['msal']['instance']) => {
        // ---------------------
        const navigationClient = new CustomNavigationClient(navigate);
        pca.setNavigationClient(navigationClient);
    }, [navigate]);
};

export const useJobSearch = (authDetail: ReturnType<typeof useMSALPayload>) => {
    // Add a ref to track if we're already handling a login redirect
    const loginRedirectInProgress = useRef(false);
    
    // Add a ref to store the current authDetail so it's always fresh in the error handler
    const authDetailRef = useRef(authDetail);
    authDetailRef.current = authDetail;

    return useCallback(async (query?: string, options?: RequestInit) => {
        // ----------------------------
        let setQuery = "";
        if (baseDefinedAndNotNullNarrowing(query)) setQuery = query;

        if (!baseDefinedAndNotNullNarrowing(authDetail)) throw new Error('Unauthorized');
        try {
            // ----------------------------
            const jobList = await searchJobList(setQuery, authDetail, options?.signal ?? undefined);
            return jobList;
        } catch (error) {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
            ) {
              // --------------------------------------------
              // Use the ref to get the current authDetail instead of the stale closure value
              const { msal } = authDetailRef.current;
            
              // Check both MSAL's inProgress state AND our local tracking
              if (msal.inProgress === "none" && !loginRedirectInProgress.current) {
                loginRedirectInProgress.current = true;

                // Use a try-catch to handle any potential errors from loginRedirect
                try {
                  // --------------------------------------------
                  await msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                  });
                  // --------------------------------------------
                  loginRedirectInProgress.current = false;
                } catch (redirectError) {
                  // If loginRedirect fails, reset our tracking
                  loginRedirectInProgress.current = false;

                  // If it's an interaction_in_progress error, that's expected
                  if (redirectError instanceof Error &&
                    redirectError.message.includes('interaction_in_progress')) {
                    console.warn('Login redirect already in progress, ignoring duplicate call');
                  } else {
                    // Re-throw unexpected errors
                    Sentry.addBreadcrumb({              // bread-crumb only, no issue
                        category: "App.tsx auth",
                        message: `Non-interaction error with loginRedirect in App.tsx`,
                        level:  "info",
                      });
                      Sentry.withScope((scope) => {
                        scope.setTag('function', 'useJobSearch');
                        scope.setLevel('error');
                        Sentry.captureException(redirectError);
                      });
                    throw redirectError;
                  }
                }
              } else {
                console.warn(`Login redirect already in progress (MSAL: ${msal.inProgress}, Local: ${loginRedirectInProgress.current})`);
              }
            }                        
            console.error('Error in useJobSearch');
            console.error(error);
            throw error;
        }
    }, [authDetail]);
};

export const useIsAdmin = (authDetail: ReturnType<typeof useMSALPayload>, enabled: boolean = false): boolean => {
    // --------------------------------
    const { isLoading: isAdminTeamLoading, data: adminTeamData } = useQuery({
        queryKey: ['adminList'],
        queryFn: async () => {
            // --------------------------------
            const { msal } = authDetail;
            const { idTokenClaims } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
            const adminTeam = await fetchMsGroup(authDetail, 'work-and-safety-admin');
            return {
                adminTeam,
                idTokenClaims: idTokenClaims as IdTokenClaims
            };
        },
        enabled,
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    // --------------------------------
    return Boolean(
        baseDefinedAndNotNullNarrowing(authDetail) 
        && !isAdminTeamLoading
        && baseDefinedAndNotNullNarrowing(adminTeamData)
        && baseDefinedAndNotNullNarrowing(adminTeamData.adminTeam.find(
            (user) => user.id === adminTeamData.idTokenClaims?.oid
        ))
    );
}


export const useAdminList = (authDetail: ReturnType<typeof useMSALPayload>, enabled: boolean = false) => {
    // --------------------------------
    const { 
        isLoading: adminListLoading,
        isError: adminListError,
        data: adminListData,
        error: adminListErrorData
    } = useQuery({
        queryKey: ['adminList'],
        queryFn: () => fetchMsGroup(authDetail, 'work-and-safety-admin'),
        enabled,
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    return {
        adminListLoading,
        adminListError,
        adminListData,
        adminListErrorData
    };
}

export const useFullTeamList = (authDetail: ReturnType<typeof useMSALPayload>, enabled: boolean = false) => {
    // --------------------------------
    const { 
        isLoading: fullTeamListLoading,
        isError: fullTeamListError,
        data: fullTeamListData,
        error: fullTeamListErrorData
    } = useQuery({
        queryKey: ['fullTeamList'],
        queryFn: () => fetchMsGroup(authDetail, 'all-staff'),
        enabled,
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    return {
        fullTeamListLoading,
        fullTeamListError,
        fullTeamListData,
        fullTeamListErrorData
    };
}


export const useIdentityByEmail = (authDetail: ReturnType<typeof useMSALPayload>, email: string, enabled: boolean = false) => {
    // --------------------------------
    const { 
        isLoading: identityLoading,
        isError: identityError,
        data: identityData,
        error: identityErrorData
    } = useQuery({
        queryKey: ['identity', email],
        queryFn: () => fetchIdentityByEmail(authDetail, email),
        enabled,
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    return {
        identityLoading,
        identityError,
        identityData,
        identityErrorData
    };
}

export const useHazardList = (authDetail: ReturnType<typeof useMSALPayload>, enabled: boolean = false, query?: { hazardClass?: string, status?: string }) => {
    // --------------------------------
    const { hazardClass, status } = query ?? {};
    const queryKey = ['hazardList'];
    if (baseDefinedAndNotNullNarrowing(hazardClass)) queryKey.push(hazardClass);
    if (baseDefinedAndNotNullNarrowing(status)) queryKey.push(status);


    const { 
        isLoading: hazardListLoading,
        isError: hazardListError,
        data: hazardListData,
        error: hazardListErrorData
    } = useQuery({
        queryKey,
        queryFn: () => fetchHazardList(authDetail, query),
        enabled,
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    return {
        hazardListLoading,
        hazardListError,
        hazardListData,
        hazardListErrorData
    };
}

export const useHazardClassList = (authDetail: ReturnType<typeof useMSALPayload>, enabled: boolean = false, query?: { status?: string }) => {
    // --------------------------------
    const { status } = query ?? {};
    const queryKey = ['hazardClassList'];
    if (baseDefinedAndNotNullNarrowing(status)) queryKey.push(status);

    const { 
        isLoading: hazardClassListLoading,
        isError: hazardClassListError,
        data: hazardClassListData,
        error: hazardClassListErrorData
    } = useQuery({
        queryKey,
        queryFn: () => fetchHazardClassList(authDetail, query),
        enabled,
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    return {
        hazardClassListLoading,
        hazardClassListError,
        hazardClassListData,
        hazardClassListErrorData
    };
}

export const useEquipmentClassList = (authDetail: ReturnType<typeof useMSALPayload>, enabled: boolean = false, query?: { type?: string}) => {
    // --------------------------------
    const { type } = query ?? {};
    const queryKey = ['equipmentClassList'];
    if (baseDefinedAndNotNullNarrowing(type)) queryKey.push(type);

    const { 
        isLoading: equipmentClassListLoading,
        isError: equipmentClassListError,
        data: equipmentClassListData,
        error: equipmentClassListErrorData
    } = useQuery({
        queryKey,
        queryFn: () => fetchEquipmentClassList(authDetail, query),
        enabled,
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    return {
        equipmentClassListLoading,
        equipmentClassListError,
        equipmentClassListData,
        equipmentClassListErrorData
    };
}

export const usePlanDetail = (
    authDetail: ReturnType<typeof useMSALPayload>,
    planId: string, enabled: boolean = false,
    retry: number = 2
) => {
    // --------------------------------
    const { 
        isLoading: planDetailLoading,
        isError: planDetailError,
        data: planDetailData,
        error: planDetailErrorData
    } = useQuery({
        queryKey: ['plan', planId],
        queryFn: () => fetchPlanDetail(authDetail, planId),
        enabled: enabled,
        refetchOnWindowFocus: true,
        retry
    });

    return {
        planDetailLoading,
        planDetailError,
        planDetailData,
        planDetailErrorData
    };
}

export const usePrestartDetail = (
    authDetail: ReturnType<typeof useMSALPayload>,
    prestartId: string,
    enabled: boolean = false,
    retry: number = 2
) => {
    // --------------------------------
    const { 
        isLoading: prestartDetailLoading,
        isError: prestartDetailError,
        data: prestartDetailData,
        error: prestartDetailErrorData
    } = useQuery({
        queryKey: ['prestart', prestartId],
        queryFn: () => fetchPrestartDetail(authDetail, prestartId),
        enabled: enabled,
        refetchOnWindowFocus: true,
        retry
    });

    return {
        prestartDetailLoading,
        prestartDetailError,
        prestartDetailData,
        prestartDetailErrorData
    };
}